import React, { Suspense } from "react";
import lazyWithRetry from "../utils/lazyWithRetry";
import { Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import {
  MenuDashBoard,
  Team,
  Orders,
  Patient,
  Reports,
  ReportCompare,
  TopRegionsV3,
  TopSoldProductsV3,
  DailyOrdersV3,
  PaymentMethodsV3,
  Analytics,
  Settings,
  Menu,
  Safe,
  MyAccount,
  Landing,
  BlogArchive,
  SingleBlog,
  MainLanding,
  Metrics,
  Terms,
  Privacy,
  MapContainer,
  MapContainerNew,
  GeneratedReports,
  PromoCodes,
  ResetPassword,
  LoggedInUsers,
  Payroll,
  PayrollDetail,
  Alpineiq,
  MyHours,
} from "../pages";
import { Drivers, Roles, TeamMembers } from "../pages/Team/components";
import "./App.scss";
import { useSelector } from "react-redux";
import useApp from "../hooks/useApp";
import { buildTheme } from "../config/theme";
import {
  ORDERS_VIEW_SUBJECTS,
  SETTINGS_VIEW_SUBJECTS,
} from "../config/constants";
import AntdStaticBridge from "../common/services/antdStatic";
import Can, { SuperuserOnly } from "../common/services/permissions/Can";
import DrakkarOnly from "../common/services/permissions/DrakkarOnly";
import NotFound from "../components/NotFound";
import {
  AllDrivers,
  Bags,
  AddBag,
  BagSales,
  BagHistory,
  BagReconciliation,
  BagAssignmentDetail,
  BagsHistory,
  PendingBagTransfers,
  CashHandovers,
  CashHandoverDriver,
} from "../pages/Team/components/Drivers/Components";
import { CreateMenuItem, EditMenuItem } from "../pages/Menu/pagescomponents";
import {
  TopSoldProducts,
  DynamicReport,
  DailyOrders,
} from "../pages/Reports/components";
import { AnalyticsPatients } from "../pages/Analytics/components";
import { AddPromotion, PromoCodesData } from "../pages/PromoCodes/Components";

const Packages = lazyWithRetry(
  () => import("../pages/Metrics/components/Packages/Packages")
);
const Sales = lazyWithRetry(
  () => import("../pages/Metrics/components/Sales/Sales")
);
const TaskManager = lazyWithRetry(
  () => import("../pages/Metrics/components/TaskManager/TaskManager")
);
const Transactions = lazyWithRetry(
  () => import("../pages/Metrics/components/Transactions/Transactions")
);
const MetrcItems = lazyWithRetry(
  () => import("../pages/Metrics/components/Items/MetrcItems")
);
const MetrcStrains = lazyWithRetry(
  () => import("../pages/Metrics/components/Strains/MetrcStrains")
);
const SyncJobs = lazyWithRetry(
  () => import("../pages/Metrics/components/SyncJobs/SyncJobs")
);
const RetailDeliveries = lazyWithRetry(
  () => import("../pages/Metrics/components/RetailDeliveries/RetailDeliveries")
);
const MetrcTags = lazyWithRetry(
  () => import("../pages/Metrics/components/Tags/MetrcTags")
);
const ActivePo = lazyWithRetry(
  () => import("../pages/Metrics/components/ActivePo/ActivePo")
);
const ViewMetrcOrder = lazyWithRetry(
  () => import("../pages/Metrics/components/ViewMetrcOrder/ViewMetrcOrder")
);
const MetrcCategories = lazyWithRetry(
  () => import("../pages/Metrics/components/Categories/MetrcCategories")
);
const Transfers = lazyWithRetry(
  () => import("../pages/Metrics/components/ComplianceTransfers/Transfers")
);
const ArchivePo = lazyWithRetry(
  () => import("../pages/Metrics/components/ArchivePo/ArchivePo")
);
const CreatePurchaseOrder = lazyWithRetry(
  () =>
    import("../pages/Metrics/components/CreatePurchaseOrder/CreatePurchaseOrder")
);
const Reconciliation = lazyWithRetry(
  () => import("../pages/Metrics/components/Reconciliation/Reconciliation")
);
const Chat = lazyWithRetry(() => import("../pages/Chat/Chat"));
const ExternalAi = lazyWithRetry(
  () => import("../pages/ExternalAi/ExternalAi")
);
const InventoryTransfers = lazyWithRetry(
  () =>
    import("../pages/Metrics/components/InventoryTransfers/InventoryTransfers")
);
const Inventory = lazyWithRetry(() => import("../pages/Inventory/Inventory"));
const AuditSession = lazyWithRetry(
  () => import("../pages/Inventory/components/AuditSession/AuditSession")
);
const DiscrepancyReport = lazyWithRetry(
  () =>
    import("../pages/Inventory/components/DiscrepancyReport/DiscrepancyReport")
);
const Fulfillment = lazyWithRetry(
  () => import("../pages/Fulfillment/Fulfillment")
);
const ShopThemeEditor = lazyWithRetry(
  () => import("../pages/ShopThemeEditor/ShopThemeEditor")
);
const FulfillmentSession = lazyWithRetry(
  () =>
    import("../pages/Fulfillment/components/FulfillmentSession/FulfillmentSession")
);

function App() {
  const { me } = useApp();
  const mode = useSelector((s) => s?.theme?.mode) || "light";
  const appTheme = buildTheme(mode);
  return (
    <div className="App">
      <ConfigProvider theme={appTheme}>
        {/* antd v6 runs cssVar mode by default and needs a real element to
            carry the css-var scope class (component={false} triggered a
            console error). display:contents keeps the wrapper out of layout:
            pages stay direct flex items of .App, exactly as before. */}
        <AntdApp component="div" style={{ display: "contents" }}>
          <AntdStaticBridge />
          <Routes>
            {/* this is can component as wen need we can leave comments */}
            <Route
              path="/"
              element={
                <Can I={"view"} a={"page"} withRedirect={true} login={false}>
                  <Suspense fallback={<></>}>
                    <Landing />
                  </Suspense>
                </Can>
              }>
              <Route
                index
                element={
                  <Suspense fallback={<></>}>
                    <MainLanding />
                  </Suspense>
                }
              />
              <Route
                path="login"
                element={
                  <Suspense fallback={<></>}>
                    <MainLanding />
                  </Suspense>
                }
              />
              <Route
                path="archive"
                element={
                  <Suspense fallback={<></>}>
                    <BlogArchive />
                  </Suspense>
                }
              />
              <Route
                path="blog/:url"
                element={
                  <Suspense fallback={<></>}>
                    <SingleBlog />
                  </Suspense>
                }
              />
              <Route
                path="privacypolicy"
                element={
                  <Suspense fallback={<></>}>
                    <Privacy />
                  </Suspense>
                }
              />
              <Route
                path="terms"
                element={
                  <Suspense fallback={<></>}>
                    <Terms />
                  </Suspense>
                }
              />
              <Route
                path="password_reset/:user_id/:token"
                element={
                  <Suspense fallback={<></>}>
                    <ResetPassword />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path="/admin"
              element={
                <Can I={"view"} a={"page"} withRedirect={true} login={true}>
                  <Suspense fallback={<></>}>
                    <MenuDashBoard />
                  </Suspense>
                </Can>
              }>
              {/* <Route path="/" element={<MenuDashBoard />}> */}
              <Route
                index
                element={
                  <Can I="view" a="menu" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Menu />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="safe"
                element={
                  <Can I="view" a="menuitemsafe" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Safe />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="menu/:menuId/:subMenuId?"
                element={
                  <Can I="view" a="menu" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Menu />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="addmenuitem/:menuId?/:subMenuId?/:externalId?"
                element={
                  <Can I="add" a="menuitem" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <CreateMenuItem />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="menu-item/:menuId?/:subMenuId?/:productId?"
                element={
                  <Can I="change" a="menuitem" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      {" "}
                      <EditMenuItem />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="team"
                element={
                  <Suspense fallback={<></>}>
                    <Team />
                  </Suspense>
                }>
                <Route
                  index
                  element={
                    <Can I="view" a="driver" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Drivers />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="drivers"
                  element={
                    <Can I="view" a="driver" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Drivers />
                      </Suspense>
                    </Can>
                  }>
                  <Route
                    path="all"
                    element={
                      <Can I="view" a="driver" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <AllDrivers />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <Bags />
                        </Suspense>
                      </Can>
                    }
                  />
                  {/* <Route path="addbag" element={<Suspense fallback={<></>}><AddBag /></Suspense>} /> */}
                  <Route
                    path="editbag/:bagId"
                    element={
                      <Can I="change" a="driverbag" withRedirect={true}>
                        {" "}
                        <Suspense fallback={<></>}>
                          <AddBag />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags/:bagId/sales"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <BagSales />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags/:bagId/history"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <BagHistory />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags/:bagId/history/:assignmentId"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <BagAssignmentDetail />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags/:bagId/reconciliation"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <BagReconciliation />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="bags-history"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <BagsHistory />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="pending-transfers"
                    element={
                      <Can I="view" a="driverbag" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <PendingBagTransfers />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="cash-handovers"
                    element={
                      <Can I="view" a="cashhandover" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <CashHandovers />
                        </Suspense>
                      </Can>
                    }
                  />
                  <Route
                    path="cash-handovers/driver/:driverId"
                    element={
                      <Can I="view" a="cashhandover" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <CashHandoverDriver />
                        </Suspense>
                      </Can>
                    }
                  />
                </Route>
                <Route
                  path="teammemebers"
                  element={
                    <Can I="view" a="canasaleuser" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <TeamMembers />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="groups"
                  element={
                    <Can I="view" a="groupinorganization" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Roles />
                      </Suspense>
                    </Can>
                  }>
                  <Route
                    index
                    element={
                      <Suspense fallback={<></>}>
                        <Roles />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
              <Route
                path="myaccount"
                element={
                  <Suspense fallback={<></>}>
                    <MyAccount />
                  </Suspense>
                }
              />
              {/* [CANA-577] The orders list needs view_order, like the left-menu entry.
                [CANA-655] Or view_orderroom: the built-in Operators role holds only that
                and uses the room tabs, so it must not be redirected away. */}
              <Route
                path="orders"
                element={
                  <Can I="view" a={ORDERS_VIEW_SUBJECTS} withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Orders />
                    </Suspense>
                  </Can>
                }
              />
              {/* Create order stays open to users without view_order (add_order only). Orders
                draws the form itself and has no Outlet, so the view_createorder child route
                that used to sit here never rendered. */}
              <Route
                path="/admin/orders/:roomid/createorder"
                element={
                  <Suspense fallback={<></>}>
                    <Orders />
                  </Suspense>
                }
              />
              <Route
                path="/admin/orders/:roomid/:patient?"
                element={
                  <Can I="view" a={ORDERS_VIEW_SUBJECTS} withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Orders />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="/admin/orders/:roomid/singleorder/:orderid"
                element={
                  <Can I="view" a={ORDERS_VIEW_SUBJECTS} withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Orders />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="promocodes"
                element={
                  <Can I="view" a="promocode" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <PromoCodes />
                    </Suspense>
                  </Can>
                }>
                <Route
                  index
                  element={
                    <Suspense fallback={<></>}>
                      <PromoCodesData />
                    </Suspense>
                  }
                />
                <Route
                  path="add"
                  element={
                    <Can I="add" a="promocode" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <AddPromotion />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path=":promoCodeId/edit"
                  element={
                    <Can I="change" a="promocode" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <AddPromotion />
                      </Suspense>
                    </Can>
                  }
                />
              </Route>
              <Route
                path="patient"
                element={
                  <Can I="view" a="patient" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Patient />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="alpineiq"
                element={
                  <Can I="view" a="patient" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Alpineiq />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="loggedinusers"
                element={
                  <Can I="view" a="logged_in_users" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <LoggedInUsers />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="payroll"
                element={
                  <Can I="view" a="payroll" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Payroll />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="payroll/:userId"
                element={
                  <Can I="view" a="payroll" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <PayrollDetail />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="my-hours"
                element={
                  <Suspense fallback={<></>}>
                    <MyHours />
                  </Suspense>
                }
              />
              <Route
                path="patient/:patientId"
                element={
                  <Can I="view" a="patient" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Patient />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="reports/compare"
                element={
                  <Can I="view" a="comparepanel" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <ReportCompare />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="reports"
                element={
                  <Can I="view" a="report" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Reports />
                    </Suspense>
                  </Can>
                }>
                <Route
                  path="topsoldproducts"
                  element={
                    <Suspense fallback={<></>}>
                      <TopSoldProducts />
                    </Suspense>
                  }
                />
                <Route
                  path="dailyorders"
                  element={
                    <Suspense fallback={<></>}>
                      <DailyOrders />
                    </Suspense>
                  }
                />
                <Route
                  path=":reportid"
                  element={
                    <Suspense fallback={<></>}>
                      <DynamicReport />
                    </Suspense>
                  }
                />
              </Route>
              {/* [CANA-891] Reports v2: same report pages, Drakkar organisation only */}
              <Route
                path="reportsv2"
                element={
                  <Can I="view" a="report" withRedirect={true}>
                    <DrakkarOnly>
                      <Suspense fallback={<></>}>
                        <Reports />
                      </Suspense>
                    </DrakkarOnly>
                  </Can>
                }>
                {/* [CANA-900] Top Sold Products on API v3 */}
                <Route
                  path="topsoldproducts"
                  element={
                    <Suspense fallback={<></>}>
                      <TopSoldProductsV3 />
                    </Suspense>
                  }
                />
                {/* [CANA-906] Daily Orders on API v3; Daily Profit is the
                    same page with Profit selected (its own key, so moving
                    between the two starts the page again) */}
                <Route
                  path="dailyorders"
                  element={
                    <Suspense key="dailyorders" fallback={<></>}>
                      <DailyOrdersV3 />
                    </Suspense>
                  }
                />
                <Route
                  path="dailyprofit"
                  element={
                    <Suspense key="dailyprofit" fallback={<></>}>
                      <DailyOrdersV3 defaultMetric="profit" />
                    </Suspense>
                  }
                />
                {/* [CANA-892] Top Regions on API v3 */}
                <Route
                  path="topregions"
                  element={
                    <Suspense fallback={<></>}>
                      <TopRegionsV3 />
                    </Suspense>
                  }
                />
                {/* [CANA-917] Payment Methods on API v3 */}
                <Route
                  path="paymentmethods"
                  element={
                    <Suspense fallback={<></>}>
                      <PaymentMethodsV3 />
                    </Suspense>
                  }
                />
                <Route
                  path=":reportid"
                  element={
                    <Suspense fallback={<></>}>
                      <DynamicReport />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="analytics"
                element={
                  <Can I="view" a="analytics" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Analytics />
                    </Suspense>
                  </Can>
                }>
                <Route index element={<Navigate to="patients" replace />} />
                <Route
                  path="patients"
                  element={
                    <Suspense fallback={<></>}>
                      <AnalyticsPatients />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="map"
                element={
                  <Can I="view" a="in_map" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <MapContainerNew />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="mapnew"
                element={
                  <Can I="view" a="in_map" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <MapContainer />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="metrc"
                element={
                  <Suspense fallback={<></>}>
                    <Metrics />
                  </Suspense>
                }>
                <Route
                  index
                  path="packages"
                  element={
                    <Can I="view" a="package" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Packages />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="sales"
                  element={
                    <Can I="view" a="receipt" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Sales />
                      </Suspense>
                    </Can>
                  }
                />
                {/* [CANA-829] Dev-only METRC pages: no grantable codename, superusers only. */}
                <Route
                  path="taskmanager"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <TaskManager />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="transactions"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <Transactions />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="items"
                  element={
                    <Can I="view" a="item" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <MetrcItems />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="categories"
                  element={
                    <Can I="metrcs.view" a="category" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <MetrcCategories />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="compliancetransfers"
                  element={
                    <Can I="view" a="transfer" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <Transfers />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="strains"
                  element={
                    <Can I="metrcs.view" a="strain" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <MetrcStrains />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="syncjobs"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <SyncJobs />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="retaildeliveries"
                  element={
                    <Can I="view" a="retailerdelivery" withRedirect={true}>
                      <Suspense fallback={<></>}>
                        <RetailDeliveries />
                      </Suspense>
                    </Can>
                  }
                />
                <Route
                  path="tags"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <MetrcTags />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="activepo"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <ActivePo />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path=":ordertype/:id/view"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <ViewMetrcOrder />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="archivepo"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <ArchivePo />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path=":ordertype/create"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <CreatePurchaseOrder />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
                <Route
                  path="reconciliation"
                  element={
                    // [CANA-828] Reads the packages and the METRC categories lists.
                    <Can I="view" a="package" withRedirect={true}>
                      <Can I="metrcs.view" a="category" withRedirect={true}>
                        <Suspense fallback={<></>}>
                          <Reconciliation />
                        </Suspense>
                      </Can>
                    </Can>
                  }
                />
                <Route
                  path="inventorytransfers"
                  element={
                    <SuperuserOnly>
                      <Suspense fallback={<></>}>
                        <InventoryTransfers />
                      </Suspense>
                    </SuperuserOnly>
                  }
                />
              </Route>{" "}
              <Route
                path="inventory"
                element={
                  <Can I="view" a="inventorytransaction" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Inventory />
                    </Suspense>
                  </Can>
                }>
                <Route
                  path="audits/:auditId"
                  element={
                    <Suspense fallback={<></>}>
                      <AuditSession />
                    </Suspense>
                  }
                />
                <Route
                  path="audits/:auditId/report"
                  element={
                    <Suspense fallback={<></>}>
                      <DiscrepancyReport />
                    </Suspense>
                  }
                />
                <Route path="*" element={null} />
              </Route>
              <Route
                path="fulfillment"
                element={
                  <Can I="view" a="driverhandoff" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Fulfillment />
                    </Suspense>
                  </Can>
                }>
                <Route
                  path=":handoffId"
                  element={
                    <Suspense fallback={<></>}>
                      <FulfillmentSession />
                    </Suspense>
                  }
                />
                <Route path="*" element={null} />
              </Route>
              <Route
                path="settings"
                element={
                  <Can I="view" a={SETTINGS_VIEW_SUBJECTS} withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Settings />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="settings/shop-theme"
                element={
                  <Can I="change" a="shopsetting" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <ShopThemeEditor />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="externalai"
                element={
                  <Can I="view" a="apikey" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <ExternalAi />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="generatedreports"
                element={
                  <Can I="view" a="generatedreport" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <GeneratedReports />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="generatedreports/:generatedReportId"
                element={
                  <Can I="view" a="generatedreport" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <GeneratedReports />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="chat"
                element={
                  <Can I="view" a="aichat" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Chat />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="chat/:chatId"
                element={
                  <Can I="view" a="aichat" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Chat />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="aihelp"
                element={
                  <Can I="use" a="help_ai" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Chat kind="help" />
                    </Suspense>
                  </Can>
                }
              />
              <Route
                path="aihelp/:chatId"
                element={
                  <Can I="use" a="help_ai" withRedirect={true}>
                    <Suspense fallback={<></>}>
                      <Chat kind="help" />
                    </Suspense>
                  </Can>
                }
              />
            </Route>
            {/* [CANA-649] Unknown URLs: "Page not found" instead of a blank screen. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AntdApp>
      </ConfigProvider>
    </div>
  );
}

export default App;
